import {
  useOptionalRouteOrganizationId,
  useRouteOrganizationId,
} from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import { QueryFilters } from '@/lib/utils'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import {
  queryOptions,
  skipToken,
  useMutation,
  useQuery,
  useSuspenseQuery,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'
import { useEffect, useEffectEvent } from 'react'
import { invalidateDesignQueries } from '../designs/designs.query'
import { schedulePriceAdjustmentBoundaryCallback } from '../price-adjustments/priceAdjustments.query'
import { invalidatePricingMutationDependencies } from '../price-adjustments/priceAdjustments.invalidation'
import { addOnSetQueryKeys } from '../add-on-sets/addOnSets.keys'
import {
  createCollection,
  createFromGlobalCollection,
  deleteCollection,
  getActiveGlobalCollectionImportAttempt,
  getCollection,
  getCollections,
  getGlobalCollectionImportAttempt,
  getGlobalCollections,
  importFromGlobalCollectionSource,
  updateCollection,
  updateCollectionMedia,
  type CollectionImportAttemptScope,
  type CollectionIndexQueryKeyEntries,
  type CollectionOrganizationScope,
  type CollectionScope,
  type CollectionShowQueryKeyEntries,
  type GlobalCollectionQueryKeyEntries,
} from './collections.api'
import type {
  CollectionIdInput,
  CollectionImportAttempt,
  CollectionImportAttemptStatus,
  CollectionIndexFilters,
  CollectionIndexResult,
  CollectionListFilters,
  CollectionMediaListInput,
  CreateCollectionInput,
  CreateFromGlobalCollectionInput,
  GlobalCollectionFilters,
  UpdateCollectionInput,
} from './collections.types'

/**
 * Hooks fall back to the route's organization; the `all` key without one matches every
 * organization's collections; the scoped key builders throw without one.
 */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface CollectionIndexScope extends OrganizationScope {
  filters?: CollectionIndexFilters | undefined
}

interface GlobalCollectionScope extends OrganizationScope {
  filters?: GlobalCollectionFilters | undefined
}

/** The show key names one collection. */
interface CollectionShowKeyScope extends OrganizationScope {
  collectionId: CollectionIdInput
}

/** Query options build requests, which need the organization. */
type CollectionIndexQueryScope = CollectionOrganizationScope & {
  filters?: CollectionIndexFilters | undefined
}

type GlobalCollectionQueryScope = CollectionOrganizationScope & {
  filters?: GlobalCollectionFilters | undefined
}

interface CollectionImportAttemptKeyScope extends OrganizationScope {
  importAttemptId?: string | null | undefined
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type CollectionIndexQueryKey = [AdminQueryRoot & CollectionIndexQueryKeyEntries]
type GlobalCollectionQueryKey = [AdminQueryRoot & GlobalCollectionQueryKeyEntries]
type CollectionShowQueryKey = [AdminQueryRoot & CollectionShowQueryKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

interface EnabledOption {
  enabled?: boolean
}

export const collectionsFilters = () =>
  new QueryFilters({ include: ['products.media', 'products.variants', 'media'] })

export const collectionMerchandisingIncludes = ['media', 'occasions', 'seasons']

export const designCollectionsFilters = {
  include: collectionMerchandisingIncludes,
  with_active_products_count: true,
  with_storefront_products_count: true,
  with_storefront_products: true,
} satisfies CollectionIndexFilters

export const collectionQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'collection' }, { organizationId }),
  ],
  global: ({ organizationId, filters }: GlobalCollectionScope): GlobalCollectionQueryKey =>
    buildScopedAdminQueryKey(collectionQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'global',
      filters,
    }),
  index: ({ organizationId, filters }: CollectionIndexScope): CollectionIndexQueryKey =>
    buildScopedAdminQueryKey(collectionQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
    }),
  show: ({ organizationId, collectionId }: CollectionShowKeyScope): CollectionShowQueryKey => {
    const [entries] = buildScopedAdminQueryKey(collectionQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
    })

    // Set after the shared builder, whose loosely typed entries would make the id optional.
    return [{ ...entries, collectionId }]
  },
  importActiveAttempt: ({ organizationId }: OrganizationScope) =>
    buildScopedAdminQueryKey(collectionQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'import-active-attempt',
    }),
  importAttempt: ({ organizationId, importAttemptId }: CollectionImportAttemptKeyScope) =>
    buildScopedAdminQueryKey(collectionQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'import-attempt',
      importAttemptId,
    }),
}

export const collectionQueries = {
  index: ({ organizationId, filters }: CollectionIndexQueryScope) =>
    queryOptions({
      queryKey: collectionQueryKeys.index({ organizationId, filters }),
      queryFn: getCollections,
    }),
  indexTable: ({ organizationId, filters }: CollectionIndexQueryScope) =>
    indexTableQueryOptions({
      queryKey: collectionQueryKeys.index({ organizationId, filters }),
      queryFn: getCollections,
    }),
  global: ({ organizationId, filters }: GlobalCollectionQueryScope) =>
    queryOptions({
      queryKey: collectionQueryKeys.global({ organizationId, filters }),
      queryFn: getGlobalCollections,
    }),
  show: ({ organizationId, collectionId }: CollectionScope) =>
    queryOptions({
      queryKey: collectionQueryKeys.show({ organizationId, collectionId }),
      queryFn: getCollection,
    }),
}

function useRefetchCollectionsAtBoundary({
  organizationId,
  boundaryAt,
  dedupeKey,
}: {
  organizationId: number
  boundaryAt: string | null | undefined
  dedupeKey: string
}) {
  const refreshCollections = useEffectEvent(async () => {
    if (!organizationId) {
      return
    }

    // Reject when the refetch fails so the boundary scheduler clears its dedupe and the next mount retries.
    await queryClient.invalidateQueries(
      { queryKey: collectionQueryKeys.all({ organizationId }) },
      { throwOnError: true }
    )
  })

  useEffect(() => {
    if (!organizationId || !boundaryAt) {
      return
    }

    return schedulePriceAdjustmentBoundaryCallback({
      boundaryAt,
      onBoundary: refreshCollections,
      dedupeKey: `collections:${dedupeKey}`,
    })
  }, [boundaryAt, dedupeKey, organizationId])
}

const invalidateCollectionAndDesignQueries = (organizationId: number) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: collectionQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: addOnSetQueryKeys.all({ organizationId }) }),
    invalidateDesignQueries({ organizationId }),
  ])

const invalidateCollectionDesignAndPricingDependencies = (
  organizationId: number | string,
  { includeCategories = false }: { includeCategories?: boolean } = {}
) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: collectionQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: addOnSetQueryKeys.all({ organizationId }) }),
    invalidateDesignQueries({ organizationId }),
    invalidatePricingMutationDependencies(organizationId, { includeCategories }),
  ])

/** A finished import changes collections, products, prices, designs and categories. */
export const invalidateCollectionImportDependencies = (organizationId: number | string) =>
  invalidateCollectionDesignAndPricingDependencies(organizationId, {
    includeCategories: true,
  })

const collectionImportCompletionWatchers = new Map<string, { clear: () => void }>()
const collectionImportCompletionPollIntervalMs = 2000

function collectionImportCompletionWatcherKey({
  organizationId,
  importAttemptId,
}: CollectionImportAttemptScope) {
  return `${organizationId}:${importAttemptId}`
}

function isRunningImportStatus(status: CollectionImportAttemptStatus | undefined) {
  return status === 'queued' || status === 'running'
}

async function invalidateCollectionImportAttemptQueries({
  organizationId,
  importAttemptId,
}: CollectionImportAttemptScope) {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: collectionQueryKeys.importActiveAttempt({ organizationId }),
    }),
    queryClient.invalidateQueries({
      queryKey: collectionQueryKeys.importAttempt({ organizationId, importAttemptId }),
    }),
  ])
}

/** Polls a queued import until it finishes, then refreshes the data it changed. */
export function watchGlobalCollectionImportAttemptCompletion({
  organizationId,
  importAttemptId,
}: CollectionImportAttemptScope) {
  if (!organizationId || !importAttemptId) {
    return
  }

  const watcherKey = collectionImportCompletionWatcherKey({ organizationId, importAttemptId })

  if (collectionImportCompletionWatchers.has(watcherKey)) {
    return
  }

  let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null

  const clear = () => {
    if (timeoutId) {
      globalThis.clearTimeout(timeoutId)
    }

    collectionImportCompletionWatchers.delete(watcherKey)
  }

  const schedule = (delayMs = collectionImportCompletionPollIntervalMs) => {
    timeoutId = globalThis.setTimeout(() => {
      void poll()
    }, delayMs)
  }

  const poll = async () => {
    try {
      const attempt = await getGlobalCollectionImportAttempt({ organizationId, importAttemptId })

      queryClient.setQueryData?.(
        collectionQueryKeys.importAttempt({ organizationId, importAttemptId }),
        attempt
      )

      if (attempt.status === 'completed') {
        await Promise.all([
          invalidateCollectionImportDependencies(organizationId),
          invalidateCollectionImportAttemptQueries({ organizationId, importAttemptId }),
        ])
        clear()

        return
      }

      if (!isRunningImportStatus(attempt.status)) {
        await invalidateCollectionImportAttemptQueries({ organizationId, importAttemptId })
        clear()

        return
      }
    } catch {
      // Keep polling through transient network/API errors while the queued import may still finish.
    }

    if (collectionImportCompletionWatchers.has(watcherKey)) {
      schedule()
    }
  }

  collectionImportCompletionWatchers.set(watcherKey, { clear })
  schedule(0)
}

export function clearGlobalCollectionImportCompletionWatchersForTest() {
  collectionImportCompletionWatchers.forEach((watcher) => watcher.clear())
}

// A non-paginated request with no selector keeps the API's array response intact.
export function useCollections(
  options?: OrganizationScope & {
    filters?: CollectionListFilters
    params?: QueryParams<ReturnType<typeof collectionQueries.index>> & { select?: undefined }
  }
): UseSuspenseQueryResult<Extract<CollectionIndexResult, unknown[]>, Error>
export function useCollections(
  options?: CollectionIndexScope & {
    params?: QueryParams<ReturnType<typeof collectionQueries.index>>
  }
): UseSuspenseQueryResult<CollectionIndexResult, Error>
export function useCollections({
  organizationId,
  filters,
  params = {},
}: CollectionIndexScope & {
  params?: QueryParams<ReturnType<typeof collectionQueries.index>>
} = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  const query = useSuspenseQuery({
    ...collectionQueries.index({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })

  useRefetchCollectionsAtBoundary({
    organizationId: resolvedOrganizationId,
    boundaryAt: query.data?.nextPriceAdjustmentChangeAt,
    dedupeKey: `index:${resolvedOrganizationId}`,
  })

  return query
}

export function useCollectionsTable({
  organizationId,
  filters,
  params = {},
}: CollectionIndexScope & {
  params?: QueryParams<ReturnType<typeof collectionQueries.indexTable>>
} = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  const query = useQuery({
    ...collectionQueries.indexTable({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })

  useRefetchCollectionsAtBoundary({
    organizationId: resolvedOrganizationId,
    boundaryAt: query.data?.nextPriceAdjustmentChangeAt,
    dedupeKey: `index-table:${resolvedOrganizationId}`,
  })

  return query
}

export function useGlobalCollections({
  organizationId,
  filters,
  params = {},
}: GlobalCollectionScope & {
  params?: QueryParams<ReturnType<typeof collectionQueries.global>>
} = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...collectionQueries.global({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useCollection(
  collectionId: CollectionIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof collectionQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  const query = useSuspenseQuery({
    ...collectionQueries.show({ organizationId: resolvedOrganizationId, collectionId }),
    ...params,
  })

  useRefetchCollectionsAtBoundary({
    organizationId: resolvedOrganizationId,
    boundaryAt: query.data?.nextPriceAdjustmentChangeAt,
    dedupeKey: `show:${resolvedOrganizationId}:${collectionId}`,
  })

  return query
}

export function useCreateCollectionMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: CreateCollectionInput) => createCollection({ organizationId, ...values }),
    onSuccess: () => invalidateCollectionAndDesignQueries(organizationId),
  })
}

/** Imports run in the background; a watcher refreshes caches when one finishes. */
function handleStartedImportAttempt(organizationId: number, attempt: CollectionImportAttempt) {
  if (isRunningImportStatus(attempt?.status)) {
    watchGlobalCollectionImportAttemptCompletion({
      organizationId,
      importAttemptId: attempt.id,
    })

    return
  }

  if (attempt?.status === 'completed') {
    void invalidateCollectionImportDependencies(organizationId)
  }
}

export function useCreateFromGlobalCollectionMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: CreateFromGlobalCollectionInput) =>
      createFromGlobalCollection({ organizationId, ...values }),
    onSuccess: (attempt) => handleStartedImportAttempt(organizationId, attempt),
  })
}

export function useImportGlobalCollectionSourceMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ sourceSystem }: { sourceSystem: string }) =>
      importFromGlobalCollectionSource({ organizationId, sourceSystem }),
    onSuccess: (attempt) => handleStartedImportAttempt(organizationId, attempt),
  })
}

export function useActiveGlobalCollectionImportAttemptQuery({
  enabled = true,
}: EnabledOption = {}) {
  const organizationId = useOptionalRouteOrganizationId()

  return useQuery({
    queryKey: collectionQueryKeys.importActiveAttempt({ organizationId }),
    enabled: Boolean(enabled && organizationId),
    queryFn: organizationId
      ? () => getActiveGlobalCollectionImportAttempt({ organizationId })
      : skipToken,
    refetchOnMount: 'always',
    retry: false,
  })
}

export function useGlobalCollectionImportAttemptQuery(
  importAttemptId: string | null | undefined,
  { enabled = true }: EnabledOption = {}
) {
  const organizationId = useOptionalRouteOrganizationId()

  return useQuery({
    queryKey: collectionQueryKeys.importAttempt({ organizationId, importAttemptId }),
    enabled: Boolean(enabled && organizationId && importAttemptId),
    queryFn:
      organizationId && importAttemptId
        ? () => getGlobalCollectionImportAttempt({ organizationId, importAttemptId })
        : skipToken,
    refetchInterval: (query) => {
      const status = query.state.data?.status

      return status === 'queued' || status === 'running' ? 2000 : false
    },
  })
}

export function useUpdateCollectionMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: UpdateCollectionInput & { collectionId: CollectionIdInput }) =>
      updateCollection({ organizationId, ...values }),
    onSuccess: () => invalidateCollectionDesignAndPricingDependencies(organizationId),
  })
}

export function useUpdateCollectionMediaMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: { collectionId: CollectionIdInput; media?: CollectionMediaListInput }) =>
      updateCollectionMedia({ organizationId, ...values }),
    onSuccess: () => invalidateCollectionAndDesignQueries(organizationId),
  })
}

const cleanupDeletedCollectionQueries = ({
  organizationId,
  collectionId,
}: {
  organizationId: number
  collectionId: CollectionIdInput
}) => {
  queryClient.removeQueries({
    queryKey: collectionQueryKeys.show({ organizationId, collectionId }),
  })

  return invalidateCollectionDesignAndPricingDependencies(organizationId)
}

export function useDeleteCollectionMutation({
  cleanupOnSuccess = true,
}: {
  /** False for callers that clean up after navigating away (`cleanupDeletedCollection`). */
  cleanupOnSuccess?: boolean
} = {}) {
  const organizationId = useRouteOrganizationId()
  const cleanupDeletedCollection = (collectionId: CollectionIdInput) =>
    cleanupDeletedCollectionQueries({ organizationId, collectionId })

  const mutation = useMutation({
    mutationFn: (collectionId: CollectionIdInput) =>
      deleteCollection({ organizationId, collectionId }),
    onSuccess: (_, collectionId) => {
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedCollection(collectionId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedCollection,
  }
}
